/**
 * Frontend URL → file_record → canvas node pipeline for onboarding.
 *
 * Modeled on `seedCanvasFiles` in `OnboardingWelcomeDialog.tsx` but
 * extended to also place each uploaded file as a node on the canvas at
 * a calculated position. Used by Steps 5 and 7 to land product images
 * and the deck HTML deterministically without waiting for the agent.
 *
 * Each URL is fetched client-side, uploaded via `performFileRecordUpload`,
 * and placed via `canvas.addNode` at the provided position. Errors per-file
 * are swallowed so a transient CDN failure doesn't strand the user.
 *
 * Returns the file_record ids of successfully placed nodes (in order of
 * the input spec).
 */

import { performFileRecordUpload } from "@/hooks/useFileRecordUpload";
import { markInsertSource } from "@/pages/canvas/analytics/insertSource";
import {
  buildFileRecordEntityForCanvas,
  DOC_PORTRAIT_NODE_WIDTH,
  readImageFileDimensions,
} from "@/pages/canvas/utils/imageDimensions";
import type { FileRecord } from "@cohesive/api-client";
import type { QueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";

// The brief PDF is a page (320×416, the canvas's default box for every
// non-media file record). The layout below only needs its width: the image
// column and the deck sit to its right, and the column's top row starts one
// row above the PDF, so the PDF's height never intersects anything.
export const PDF_NODE_WIDTH = DOC_PORTRAIT_NODE_WIDTH;

// Image column geometry (spec §6)
// Horizontal gap between PDF and image column. 80 keeps the column at the
// 400px offset the layout was tuned at when the PDF tile was 350 wide.
export const IMAGE_COLUMN_GAP = 80;
export const IMAGE_ROW_HEIGHT = 250; // image height + 50px vertical gap

// Deck geometry (spec §8). The deck html lands as a page too (320×416); the
// width is what the column math needs.
export const IMAGE_NODE_WIDTH = 240; // approximate image node width
export const DECK_COLUMN_GAP = 50;

/**
 * Compute the three image positions for the vertical column to the
 * right of the PDF, anchored off `pdfDropPoint`.
 */
export function imageColumnPositions(pdfDropPoint: {
  x: number;
  y: number;
}): { x: number; y: number }[] {
  const columnX = pdfDropPoint.x + PDF_NODE_WIDTH + IMAGE_COLUMN_GAP;
  const topY = pdfDropPoint.y - IMAGE_ROW_HEIGHT;
  return [
    { x: columnX, y: topY },
    { x: columnX, y: topY + IMAGE_ROW_HEIGHT },
    { x: columnX, y: topY + 2 * IMAGE_ROW_HEIGHT },
  ];
}

/**
 * Compute the deck position to the right of the image column, aligned
 * with the middle image.
 */
export function deckPosition(pdfDropPoint: { x: number; y: number }): {
  x: number;
  y: number;
} {
  const columnX = pdfDropPoint.x + PDF_NODE_WIDTH + IMAGE_COLUMN_GAP;
  const topY = pdfDropPoint.y - IMAGE_ROW_HEIGHT;
  // +75px nudge keeps the deck visually clear of the image column's
  // right edge at the current onboarding zoom.
  const deckX = columnX + IMAGE_NODE_WIDTH + DECK_COLUMN_GAP + 75;
  const deckY = topY + IMAGE_ROW_HEIGHT; // middle image y
  return { x: deckX, y: deckY };
}

export type SeedSpec =
  | {
      kind: "image";
      urls: readonly string[];
      names: readonly string[];
      positions: { x: number; y: number }[];
    }
  | {
      kind: "html";
      url: string;
      name: string;
      position: { x: number; y: number };
    };

interface CanvasNodeApi {
  addNode: (node: Node) => void;
}

/**
 * Seed onboarding artifacts from public CDN URLs onto the canvas.
 * Fire-and-forget; individual URL failures are logged but don't throw.
 */
export async function seedOnboardingArtifacts(
  canvasId: string,
  queryClient: QueryClient,
  canvas: CanvasNodeApi,
  spec: SeedSpec,
): Promise<string[]> {
  const placements: {
    url: string;
    name: string;
    mimeType: string;
    position: { x: number; y: number };
  }[] =
    spec.kind === "image"
      ? spec.urls.map((url, i) => ({
          url,
          name: spec.names[i] ?? `AeroCarry Product Image ${i + 1}.jpg`,
          mimeType: "image/jpeg",
          position: spec.positions[i] ?? { x: 0, y: 0 },
        }))
      : [
          {
            url: spec.url,
            name: spec.name,
            mimeType: "text/html",
            position: spec.position,
          },
        ];

  const placedIds: string[] = [];

  await Promise.all(
    placements.map(async ({ url, name, mimeType, position }) => {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const blob = await res.blob();
        const file = new File([blob], name, { type: mimeType });

        let naturalWidth: number | undefined;
        let naturalHeight: number | undefined;
        if (mimeType.startsWith("image/")) {
          try {
            const dims = await readImageFileDimensions(file);
            if (dims) {
              naturalWidth = dims.width;
              naturalHeight = dims.height;
            }
          } catch {
            // fall back to default sizing
          }
        }

        await performFileRecordUpload({
          file,
          target: { class: "canvas", canvas_id: canvasId },
          queryClient,
          onRecordCreated: (record: FileRecord) => {
            // The builder fits images to their natural aspect and gives the
            // html deck the same page box every other route mints.
            const { entity, nodeWidth, nodeHeight } =
              buildFileRecordEntityForCanvas({
                record,
                contentType: mimeType,
                naturalWidth,
                naturalHeight,
              });
            markInsertSource("onboarding_seed");
            canvas.addNode({
              id: record.id,
              type: "entity",
              position,
              width: nodeWidth,
              height: nodeHeight,
              data: { entity },
            } as Node);
            placedIds.push(record.id);
          },
        });
      } catch {
        // Swallow per-file errors — CDN hiccup shouldn't strand the user.
      }
    }),
  );

  return placedIds;
}
